import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, type Variants } from 'framer-motion';
import { NextGame } from '../components/schedule/NextGame';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Seo } from '../components/Seo';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

export function HomePage() {
  const { t } = useTranslation('home');

  return (
    <div>
      <Seo title="Queensland Mafia Club" description="Клуб спортивной мафии в Брисбене. Играем каждую субботу в Banyo Library. Присоединяйтесь!" path="/" />
      {/* ── Hero ── */}
      <div className="relative min-h-[88vh] flex items-center justify-center overflow-hidden bg-mafia-black">
        {/* Pinstripe layer */}
        <div className="absolute inset-0 bg-pinstripe" />
        {/* Radial gold glow from top */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_50%_0%,rgba(201,162,55,0.06),transparent)]" />
        {/* Vignette from bottom */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-mafia-black/70" />

        {/* Corner ornaments */}
        <div className="absolute top-8 left-8 w-20 h-20 border-t border-l border-mafia-gold/25 pointer-events-none" />
        <div className="absolute top-8 right-8 w-20 h-20 border-t border-r border-mafia-gold/25 pointer-events-none" />
        <div className="absolute bottom-8 left-8 w-20 h-20 border-b border-l border-mafia-gold/25 pointer-events-none" />
        <div className="absolute bottom-8 right-8 w-20 h-20 border-b border-r border-mafia-gold/25 pointer-events-none" />

        {/* Hero content */}
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          {/* Logo */}
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex justify-center mb-8"
          >
            <img
              src="/logo.jpg"
              alt="Queensland Mafia Club"
              className="w-44 h-44 md:w-56 md:h-56 rounded-full"
              style={{ filter: 'drop-shadow(0 0 40px rgba(201,162,55,0.35))' }}
            />
          </motion.div>

          {/* Title */}
          <motion.h1
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="font-display font-bold text-mafia-gold leading-none mb-2"
            style={{ fontSize: 'clamp(2.4rem, 7vw, 5.5rem)' }}
          >
            Queensland Mafia Club
          </motion.h1>

          <motion.div custom={2} variants={fadeUp} initial="hidden" animate="visible">
            <OrnamentDivider className="my-7 max-w-sm mx-auto" />
          </motion.div>

          <motion.p
            custom={3}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="text-mafia-paper-dim text-lg leading-relaxed mb-10 max-w-xl mx-auto"
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            custom={4}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/reserve" className="btn-gold-solid px-10 py-4">
              {t('hero.reserve')}
            </Link>
            <Link to="/rules" className="btn-gold px-10 py-4">
              {t('hero.rules')}
            </Link>
          </motion.div>
        </div>
      </div>

      {/* ── Main content ── */}
      <PageWrapper>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Club info card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="card-vintage p-8"
          >
            <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-3">
              ◆ О клубе
            </p>
            <h2 className="font-display text-2xl font-bold text-mafia-paper mb-4">
              {t('about.title')}
            </h2>
            <p className="text-mafia-paper-dim leading-relaxed mb-3">{t('about.p1')}</p>
            <p className="text-mafia-paper-dim leading-relaxed">{t('about.p2')}</p>
            <Link
              to="/about"
              className="inline-block mt-5 text-mafia-gold hover:text-mafia-gold-light text-xs font-mono tracking-widest uppercase transition-colors"
            >
              {t('about.more')} →
            </Link>
          </motion.div>

          {/* Next game */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <NextGame />
          </motion.div>
        </div>

        <OrnamentDivider className="mb-10" variant="dim" />

        {/* Quick links */}
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { to: '/rules', icon: '📖', label: t('quickLinks.rules') },
            { to: '/scoreboard', icon: '🏆', label: t('quickLinks.scoreboard') },
            { to: '/links', icon: '🌐', label: t('quickLinks.links') },
          ].map((item, i) => (
            <motion.div
              key={item.to}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
            >
              <Link
                to={item.to}
                className="card-vintage block p-6 text-center hover:bg-mafia-smoke transition-colors group"
              >
                <span className="text-3xl block mb-3">{item.icon}</span>
                <span className="font-mono text-xs text-mafia-paper-dim tracking-widest uppercase group-hover:text-mafia-gold transition-colors">
                  {item.label}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </PageWrapper>
    </div>
  );
}
